/*
 * File: ProductSelectionCard.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 20 Dec 2021 11:59:23 am
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Card, Col, Divider, Form, InputNumber, message, Row, Space, Tooltip, Typography } from 'antd';
import { MessageType } from 'antd/lib/message';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { ReleasedProductModel } from 'models/ReleasedProductModel';
import { SalesOrderLineItemModel } from 'models/SalesOrderLineItemModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from 'redux/rootReducer';
import { useGetConditionsQuery } from 'redux/services/useAnimalService';
import { v4 } from 'uuid';
import { AddButton } from './AddButton';

interface ProductSelectionCardProps {
  product: ReleasedProductModel;
  record?: SalesOrderLineItemModel;
}

const styles: InlineStylesModel = {
  cardBody: {
    borderRadius: 10,
    padding: 0,
    boxShadow: '0 0 4px rgb(0 0 0 / 10%)'
  },
  customerInfo: {
    marginTop: 10
  },
  title: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(0, 0, 0, .4)'
  },
  manufacturerText: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(0, 0, 0, .4)'
  },
  addButtonContainer: {
    position: 'absolute',
    right: 40,
    top: '50%',
    transform: 'translateY(-50%)',
    transition: '.5s width ease-out',
    // borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    height: 30,
    width: 30,
    color: 'white',
    cursor: 'pointer',
    zIndex: 1111
  },
  quantityTitle: {
    fontSize: '.8rem',
    marginBottom: 7,
    color: 'rgba(0, 0, 0, .4)'
  },
  titleWhite: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(255, 255, 255, .4)'
  },
  values: {
    fontSize: 16,
    fontWeight: 600
  },
  valuesWhite: {
    color: 'rgba(255, 255, 255, .9)',
    fontSize: 16,
    fontWeight: 600
  },
  minusButton: {
    color: '#F93943',
    borderColor: '#F93943'
  },
  selectedStyles: {
    transition: '.25s all ease-out',
    background: '#4e937aab',
    boxShadow: '0 0 6px rgba(0,0,0,.4)',
    borderRadius: 10
  },
  icon: {
    position: 'absolute',
    top: 10,
    right: 10,
    fontWeight: 'bolder',
    fontSize: 30,
    borderRadius: '50%',
    transition: '1s all ease-out'
  },
  triangle: {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '183px',
    height: '101px',
    borderLeft: 'solid 50.5px rgba(0,0,0,.2)',
    borderBottom: 'solid 50.5px transparent',
    borderTop: 'solid 50.5px transparent'
  }
};

export const ProductSelectionCard = ({ product, record }: ProductSelectionCardProps): JSX.Element => {
  const [form] = Form.useForm<SalesOrderLineItemModel>();
  const { values: formikVals, setValues } = useFormikContext<SalesOrderPayloadModel>();

  const dispatch = useDispatch();
  const { data: conditions } = useGetConditionsQuery({ overrideSkipTake: true, offset: 0 });
  const { isChild, products } = useSelector((state: ReduxState) => state.productsInfo);

  const newProductInitialValues = {
    conditionId: 'NEW',
    quantity: 1,
    unitCost: 0,
    unitPrice: 0
  };

  const handleAddProduct = (values: SalesOrderLineItemModel): void | MessageType => {

    const lineItemToAdd: SalesOrderPayloadModel['lines'][number] = {
      id: v4(),
      category: product.productGroupId,
      description: product.productDescription,
      customerLineNumber: '',
      manufacturer: product.manufacturerId,
      productNumber: product.productNumber,
      quantity: Number(values.quantity),
      siteId: formikVals.siteId,
      unitCost: Number(values.unitCost),
      unitPrice: Number(values.unitPrice),
      warehouseId: formikVals.warehouseId,
      conditionId: '',
      ownerId: '',
      dispositionId: '',
      deliveryType: 'stock',
      extendedPrice: Number(values.quantity || 1) * Number(values.unitPrice),
      totalCost: 0,
      vendorId: '',
      lineNumber: formikVals.lines.length + 1,
      alternateItemId: product.alternateItemId,
      isProductNumberValidated: true,
      isSiteValidated: formikVals.siteId ? true : false,
      isWarehouseValidated: formikVals.warehouseId ? true : false,
      isCategoryValidated: product.productGroupId ? true : false,
      isConditionValidated: false,
      isDeliveryTypeValidated: false,
      isDispositionValidated: false,
      isManufacturerValidated: false,
      isOwnerValidated: false,
      isPosted: false,
      isVendorValidated: false
    };

    setValues((prevValues) => ({
      ...prevValues,
      lines: [...prevValues.lines, lineItemToAdd]
    }));

    message.success('Line successfully added');
    form.resetFields();
  };

  const handleUnitPriceChange = (): void => {
    const { unitPrice } = form.getFieldsValue();

    form.setFieldValue('listDiscountPercentage', undefined);
    form.setFieldValue('listPrice', unitPrice);
  };

  return (
    <Card bordered={false} style={styles.cardBody} bodyStyle={styles.cardBody}>
      <Form layout="vertical" name={`addProduct${product.productNumber}Modal`} form={form} onFinish={handleAddProduct} initialValues={newProductInitialValues}>
        <Row style={{ borderRadius: 10, height: '100%' }}>
          <Col span={6} style={{ background: 'rgba(0,0,0,.2)', padding: '10px 20px', borderRadius: '10px 0 0 10px' }}>
            <Typography.Paragraph style={styles.title}>Product Number</Typography.Paragraph>
            <Typography.Paragraph style={styles.values} ellipsis>
              <Tooltip
                title={
                  <>
                    <Typography.Text style={{ color: 'white' }}>Product Name: {product.productName ?? product.searchName}</Typography.Text>
                    <Typography.Text style={{ color: 'white' }}>Product Description: {product.productDescription}</Typography.Text>
                  </>
                }>
                <Typography.Text style={{ fontWeight: 400 }}>{product.productNumber}</Typography.Text>
              </Tooltip>
            </Typography.Paragraph>
            <Typography.Paragraph style={styles.manufacturerText} ellipsis>
              <Tooltip title={`Manufacturer: ${product.manufacturerId}`}>{product.manufacturerId || 'N/A'}</Tooltip>
              <Divider type="vertical" />
              <Tooltip title={`Category: ${product.productGroupId}`}>{product.productGroupId}</Tooltip>
            </Typography.Paragraph>
          </Col>
          <Space style={styles.addButtonContainer}>
            <AddButton />
          </Space>
          <Col span={18}>
            <div style={styles.triangle} />
            <Row justify="center">
              <Col style={styles.customerInfo} span={20}>
                <Row gutter={[10, 0]} justify="space-around" align="middle">
                  <Col span={3}>
                    <Form.Item label={<Typography.Paragraph style={styles.title}>Quantity</Typography.Paragraph>} name={'quantity'}>
                      <InputNumber min={1} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item label={<Typography.Paragraph style={styles.title}>Unit Cost</Typography.Paragraph>} name={'unitCost'}>
                      <InputNumber min={0} formatter={(value): string => `$ ${value}`} step="0.01" />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item label={<Typography.Paragraph style={styles.title}>Unit Price</Typography.Paragraph>} name={'unitPrice'}>
                      <InputNumber min={0} formatter={(value): string => `$ ${value}`} onChange={handleUnitPriceChange} step="0.01" />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
